import { isEmpty } from '@/src/utils/generalUtils'
import { logError } from '@/src/utils/logError'

/**
 * The 25 vital custom attributes GA is allowed to receive.
 *
 * GA was discarding values when the payload carried too many params, so the
 * native iOS/Android bridges are restricted to this whitelist. The web
 * dataLayer push is left unfiltered (full payload) as before. `event` is
 * intentionally NOT in this list — it is the structural dataLayer/GA4 event
 * name (used to fire the tag), not a custom attribute, and is always preserved
 * separately in filterGtmPayload.
 */
export const VITAL_GTM_KEYS = [
    'trip_type',
    'source_airport_code',
    'destination_airport_code',
    'travelers_total_count',
    'flight_class',
    'product_category',
    'user_language',
    'airline_names',
    'revenue',
    'booking_id',
    'booking_payment_method',
    'user_email',
    'user_phone',
    'platform',
    // 'destination_airport_city',
    // 'source_airport_city',
    // 'user_gender',
    // 'user_nationality',
    // 'user_age',
    // 'user_birth_date',
    'user_currency',
    'user_country',
    // 'user_domain',
    'coupon_code'
] as const

/** Structural key that must always survive the filter so GTM can fire the tag. */
const GTM_EVENT_KEY = 'event'

const VITAL_GTM_KEY_SET = new Set<string>([GTM_EVENT_KEY, ...VITAL_GTM_KEYS])

/**
 * Payload key (as emitted by the hooks, i.e. the web/old names) -> the GA
 * attribute name expected on the native bridges. Web keeps the original keys
 * (this map is only applied inside filterGtmPayload, which is mobile-only), so
 * the native payloads land on the sheet's names without touching web.
 */
const GTM_KEY_ALIASES: Record<string, string> = {
    page_language: 'user_language',
    booking_flight_total_cost: 'revenue'
}

/** null / undefined / empty-or-whitespace string — values GA should not receive. */
const isBlankGtmValue = (value: any) =>
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')

/**
 * Keep only `event` + the 25 whitelisted custom attributes, dropping any that
 * are null / undefined / blank. Source keys are mapped through GTM_KEY_ALIASES
 * first (e.g. page_language -> user_language), so the native bridges receive the
 * sheet's GA names. Legitimate 0 and false are preserved, so numeric counts and
 * boolean-ish flags are not accidentally lost. Keys absent from the payload are
 * simply not emitted. NOTE: mobile-only — the web dataLayer push is unfiltered.
 */
export const filterGtmPayload = (payload: any = {}) => {
    if (isEmpty(payload)) return payload

    return Object.keys(payload).reduce((filtered: Record<string, any>, key) => {
        const mappedKey = GTM_KEY_ALIASES[key] || key
        const value = payload[key]
        if (VITAL_GTM_KEY_SET.has(mappedKey) && !isBlankGtmValue(value)) {
            filtered[mappedKey] = value
        }
        return filtered
    }, {})
}

/**
 * Events pushed to the native bridges as-is, bypassing the 25-key whitelist.
 * GA4 standard / ecommerce events (e.g. 'purchase') carry reserved fields
 * (transaction_id, value, currency, items) that are not custom attributes and
 * must not be trimmed.
 */
const FILTER_EXEMPT_EVENTS = new Set<string>(['purchase'])

/** Payload for the native bridges: exempt events pass through, everything else is filtered to the 25 keys. */
const getNativeGtmPayload = (rawPayload: any) =>
    FILTER_EXEMPT_EVENTS.has(rawPayload?.event) ? rawPayload : filterGtmPayload(rawPayload)

export const pushGtmEvent = (rawPayload: any = {}) => {


    try {

        if (typeof window !== 'undefined' && !isEmpty(rawPayload)) {
            if (window?.webkit?.messageHandlers?.GAInterface?.postMessage) {
                // iOS: 25 vital attributes only (unless the event is exempt)
                const nativePayload = getNativeGtmPayload(rawPayload)
                window.webkit.messageHandlers.GAInterface.postMessage({
                    event: nativePayload.event,
                    parameters: nativePayload
                })
            } else if (window?.MobileApp?.postMessage) {
                // Android: 25 vital attributes only (unless the event is exempt)
                const nativePayload = getNativeGtmPayload(rawPayload)
                window.MobileApp.postMessage(nativePayload.event, JSON.stringify(nativePayload))
            } else if (window.dataLayer && Array.isArray(window.dataLayer)) {
                // Web: keep the full payload as before (no filtering)
                window.dataLayer.push(rawPayload)
            }
        }
    } catch (error) {
        logError('pushGtmEvent', error)
    }
}
